import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
} from "@mui/material";
import { useQuery, useMutation } from "@apollo/client";
import { GET_ME, UPDATE_USER_FEE_SETTINGS } from "../graphql/users";
import { User, UpdateUserFeeSettingsInput } from "../types/user";

interface FeeSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved?: (user: User) => void;
}

export const FeeSettingsDialog: React.FC<FeeSettingsDialogProps> = ({
  open,
  onClose,
  onSaved,
}) => {
  const { data, loading, refetch } = useQuery(GET_ME, { skip: !open });
  const [updateFeeSettings, { loading: saving }] = useMutation(
    UPDATE_USER_FEE_SETTINGS,
  );

  const [form, setForm] = useState<UpdateUserFeeSettingsInput>({
    defaultTradeFee: 0,
    defaultPositionFee: 0,
    feeCurrency: "USD",
  });

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (data?.me) {
      setForm({
        defaultTradeFee: Number(data.me.defaultTradeFee) || 0,
        defaultPositionFee: Number(data.me.defaultPositionFee) || 0,
        feeCurrency: data.me.feeCurrency ?? "USD",
      });
    }
  }, [data, open]);

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | { target: { name?: string; value: unknown } },
  ) => {
    const { name, value } = e.target;
    if (name) {
      let newValue: any = value;

      // Handle numeric inputs for fee fields
      if (name === "defaultTradeFee" || name === "defaultPositionFee") {
        // For numeric fields, convert empty string to 0, otherwise convert to number
        newValue = value === "" ? 0 : Number(value);
        // Ensure the value is a valid number and not NaN
        if (isNaN(newValue)) {
          newValue = 0;
        }
      }

      setForm((prev) => ({
        ...prev,
        [name]: newValue,
      }));
    }
  };

  const handleSave = async () => {
    if (!data?.me) return;

    try {
      setError(null);
      setSuccess(null);

      // Ensure we have valid numeric values before submission
      const validatedForm = {
        ...form,
        defaultTradeFee: Number(form.defaultTradeFee) || 0,
        defaultPositionFee: Number(form.defaultPositionFee) || 0,
      };

      const result = await updateFeeSettings({
        variables: {
          feeSettings: validatedForm,
        },
      });

      if (result.data?.updateUserFeeSettings) {
        setSuccess("Fee settings updated successfully!");
        await refetch(); // Refresh the data
        if (onSaved) onSaved({ ...data.me, ...validatedForm } as User);
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update fee settings",
      );
    }
  };

  const handleClose = () => {
    setError(null);
    setSuccess(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div">
          Fee Settings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Configure default fees for trades and positions
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={3} mt={1}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Default fees are applied to new trades and positions. You can
              override these on a per-trade basis.
            </Typography>
          </Box>

          <Divider />

          <TextField
            label="Default Trade Fee (%)"
            name="defaultTradeFee"
            type="number"
            value={form.defaultTradeFee || ""}
            onChange={handleChange}
            fullWidth
            disabled={loading}
            inputProps={{
              step: 0.01,
              min: 0,
              max: 100,
            }}
            helperText="Percentage fee applied to new trades (e.g., 0.25 for 0.25%)"
          />

          <TextField
            label="Default Position Fee (%)"
            name="defaultPositionFee"
            type="number"
            value={form.defaultPositionFee || ""}
            onChange={handleChange}
            fullWidth
            disabled={loading}
            inputProps={{
              step: 0.01,
              min: 0,
              max: 100,
            }}
            helperText="Percentage fee applied to new positions (e.g., 0.5 for 0.5%)"
          />

          <FormControl fullWidth disabled={loading}>
            <InputLabel>Fee Currency</InputLabel>
            <Select
              name="feeCurrency"
              value={form.feeCurrency || "USD"}
              label="Fee Currency"
              onChange={handleChange}
            >
              <MenuItem value="USD">USD - US Dollar</MenuItem>
              <MenuItem value="EUR">EUR - Euro</MenuItem>
              <MenuItem value="GBP">GBP - British Pound</MenuItem>
              <MenuItem value="JPY">JPY - Japanese Yen</MenuItem>
              <MenuItem value="CAD">CAD - Canadian Dollar</MenuItem>
              <MenuItem value="AUD">AUD - Australian Dollar</MenuItem>
            </Select>
          </FormControl>

          <Box
            sx={{
              p: 2,
              bgcolor: "grey.50",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "grey.200",
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Current Settings Summary:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Trade Fee: {form.defaultTradeFee || 0}% {form.feeCurrency}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Position Fee: {form.defaultPositionFee || 0}% {form.feeCurrency}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Currency: {form.feeCurrency}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || loading}
        >
          {saving ? "Saving..." : "Save Fee Settings"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
